import {
  BookingParticipant,
  BookingSummary,
  CalBookingPayload,
  CalPerson,
} from "@/lib/types/cal";

// Wall-clock time in a given IANA zone. Used for the live visitor/studio clocks,
// so a visitor can see the office-hours overlap before picking a slot.
export const timeIn = (timeZone: string, date: Date) =>
  date.toLocaleTimeString("en-US", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

// Calendar date in a given zone, e.g. "Mon, Jul 21". The zone matters: at the
// edges of the day the studio and the visitor are on different dates.
export const dateIn = (timeZone: string, date: Date) =>
  date.toLocaleDateString("en-US", {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
  });

// Best-effort city from an IANA zone, e.g. "Asia/Dhaka" → "Dhaka",
// "America/New_York" → "New York".
//
// This is the zone's REPRESENTATIVE city, not the visitor's actual one — a
// visitor in Chattogram reads "Dhaka", one in Manchester reads "London". Real
// city detection needs geo-IP, which means a third-party lookup on every visit
// and the privacy/latency cost that comes with it. The zone is already in the
// browser, costs nothing, and is accurate enough to compare working hours.
export const cityFromZone = (timeZone: string) =>
  timeZone.split("/").pop()?.replace(/_/g, " ") ?? timeZone;

// The visitor's own zone, with a safe fallback if Intl is unavailable.
export const resolveViewerZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

// Normalise Cal.com's `bookingSuccessful` payload into something safe to
// render. Nothing is trusted: the payload arrives from a third-party iframe, so
// every field is probed and bad dates collapse to null rather than "Invalid Date".
// Cal reports the meeting location as an internal integration id such as
// "integrations:daily". Shown raw it reads like a bug, so map the known ones to
// the names people actually recognise.
const LOCATION_LABELS: Record<string, string> = {
  "integrations:daily": "Cal Video",
  "integrations:google:meet": "Google Meet",
  "integrations:zoom": "Zoom",
  "integrations:msteams": "Microsoft Teams",
  "integrations:whereby_video": "Whereby",
  "integrations:huddle01": "Huddle01",
  "integrations:tandem": "Tandem",
  "integrations:jitsi": "Jitsi Meet",
  inPerson: "In person",
  link: "Custom link",
  phone: "Phone call",
  userPhone: "Phone call",
  attendeeInPerson: "In person",
};

export const locationLabel = (raw?: string | null) => {
  const value = raw?.trim();
  if (!value) return null;
  if (LOCATION_LABELS[value]) return LOCATION_LABELS[value];
  // A real URL is friendlier as its host than as a 120-character string.
  if (/^https?:\/\//i.test(value)) {
    try {
      return new URL(value).hostname.replace(/^www\./, "");
    } catch {
      return value;
    }
  }
  // Unknown "integrations:foo:bar" — surface the most specific segment rather
  // than the internal prefix.
  if (value.startsWith("integrations:")) {
    const part = value.split(":").filter(Boolean).pop() ?? value;
    return part.charAt(0).toUpperCase() + part.slice(1);
  }
  return value;
};

// Walks a list of dot-paths and returns the first value that is actually there.
// Cal's booking events do not share one shape: the original nests everything
// under `booking`, the V2 events send the same fields flat at the root, and
// individual keys have moved between releases. Reading only `booking.x` meant a
// V2 payload produced a confirmation card with nothing on it — every field came
// back undefined and each conditional quietly collapsed.
const pick = <T>(source: unknown, paths: string[]): T | undefined => {
  for (const path of paths) {
    let current: unknown = source;
    for (const key of path.split(".")) {
      if (current === null || typeof current !== "object") {
        current = undefined;
        break;
      }
      current = (current as Record<string, unknown>)[key];
    }
    if (current !== undefined && current !== null && current !== "") {
      return current as T;
    }
  }
  return undefined;
};

// Last resort for finding the person who booked: sweep the whole payload for
// anything carrying an email address.
//
// The named paths cover the shapes Cal has actually shipped, but they keep
// moving, and a missed one shows up as a confirmation card listing only the
// host — which reads as "your booking did not register". A depth-capped walk
// costs nothing on an object this size and survives the next rename.
const MAX_SCAN_DEPTH = 6;

const scanForPeople = (source: unknown, found: CalPerson[], depth = 0) => {
  if (depth > MAX_SCAN_DEPTH || source === null || typeof source !== "object") {
    return;
  }
  if (Array.isArray(source)) {
    for (const item of source) scanForPeople(item, found, depth + 1);
    return;
  }
  const record = source as Record<string, unknown>;
  const email = record.email;
  if (typeof email === "string" && email.includes("@")) {
    found.push({
      name: typeof record.name === "string" ? record.name : undefined,
      email,
    });
  }
  for (const value of Object.values(record)) {
    scanForPeople(value, found, depth + 1);
  }
};

const toDate = (value: unknown) => {
  if (typeof value !== "string" && !(value instanceof Date)) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const toBookingSummary = (
  payload: CalBookingPayload | undefined,
  fallbackTitle: string,
  hostFallback: string,
): BookingSummary => {
  const start = toDate(
    pick(payload, [
      "booking.startTime",
      "startTime",
      "bookingInfo.startTime",
      "booking.start",
      "start",
      "date",
    ]),
  );
  const end = toDate(
    pick(payload, [
      "booking.endTime",
      "endTime",
      "bookingInfo.endTime",
      "booking.end",
      "end",
    ]),
  );

  const name = (p?: CalPerson) => p?.name?.trim() || null;
  const email = (p?: CalPerson) => p?.email?.trim() || null;

  // Cal has moved the host between `organizer` and `user` across versions.
  const host = pick<CalPerson>(payload, [
    "booking.organizer",
    "organizer",
    "bookingInfo.organizer",
    "booking.user",
    "user",
  ]);

  const attendees =
    pick<CalPerson[]>(payload, [
      "booking.attendees",
      "attendees",
      "bookingInfo.attendees",
    ]) ?? [];

  // Some payloads carry no `attendees` array at all and only the raw form
  // answers. Without this the card showed a lone host and looked like the
  // person who just booked had been dropped, so the responses are used as the
  // booker of last resort. `name` is a string normally, but an object in
  // Cal's split first/last-name mode.
  const responseName = pick<string | { firstName?: string; lastName?: string }>(
    payload,
    ["booking.responses.name", "responses.name", "bookingInfo.responses.name"],
  );
  const bookerName =
    typeof responseName === "string"
      ? responseName.trim()
      : [responseName?.firstName, responseName?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
  const bookerEmail = pick<string>(payload, [
    "booking.responses.email",
    "responses.email",
    "bookingInfo.responses.email",
  ]);

  // Whatever the payload's shape, end up with at least the person who booked:
  // the named attendee list, then the raw form answers, then a sweep of the
  // whole object for an email that is not the host's.
  const scanned: CalPerson[] = [];
  scanForPeople(payload, scanned);
  const hostEmail = email(host)?.toLowerCase();
  const seen = new Set<string>();
  const others = scanned.filter((p) => {
    const key = p.email?.toLowerCase();
    if (!key || key === hostEmail || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const people: CalPerson[] = attendees.length
    ? attendees
    : bookerName || bookerEmail
      ? [{ name: bookerName || undefined, email: bookerEmail }]
      : others;

  // Deliberately NOT de-duplicated by name: booking your own event type gives
  // the host and the attendee the same name, and dropping one made it look like
  // the guest was missing. Both sides are always shown, distinguished by role.
  const participants: BookingParticipant[] = [
    {
      name: name(host) ?? hostFallback,
      email: email(host),
      role: "host" as const,
    },
    ...people.map((a, i) => ({
      name: name(a) ?? "Guest",
      email: email(a),
      // The first attendee is whoever just filled in the form.
      role: (i === 0 ? "you" : "guest") as "you" | "guest",
    })),
  ];

  // Prefer a stated duration, but derive it from the times when Cal only sends
  // a start and an end — otherwise the "Add to calendar" button never renders.
  const statedDuration = pick<number>(payload, [
    "duration",
    "booking.duration",
    "eventType.length",
    "booking.eventType.length",
  ]);
  const derivedDuration =
    start && end ? Math.round((end.getTime() - start.getTime()) / 60000) : null;

  return {
    title:
      pick<string>(payload, [
        "booking.title",
        "title",
        "eventType.title",
        "booking.eventType.title",
      ]) ?? fallbackTitle,
    start,
    duration: statedDuration ?? derivedDuration,
    participants,
    location: locationLabel(
      pick<string>(payload, [
        "booking.location",
        "location",
        "bookingInfo.location",
        "eventType.locations.0.type",
        "booking.metadata.videoCallUrl",
        "videoCallUrl",
      ]),
    ),
    uid:
      pick<string>(payload, [
        "booking.uid",
        "uid",
        "bookingUid",
        "booking.id",
      ])?.toString() ?? null,
    // Overwritten by the caller, which knows which Cal event fired.
    rescheduled: false,
  };
};

// Folds a second report of the same booking into the first.
//
// Cal emits both `bookingSuccessful` and its V2 alias for a single booking,
// milliseconds apart, and the two payloads are not equivalent: V2 carries the
// times, duration and location but no person objects. Handled independently,
// whichever arrived last simply replaced the other — so a complete card was
// overwritten a frame later by one listing only the host. Every field keeps
// whichever version actually has it, and the participant list never shrinks.
export const mergeBookingSummary = (
  previous: BookingSummary | null,
  next: BookingSummary,
): BookingSummary => {
  if (!previous) return next;

  // A genuinely different booking — a reschedule, or a second one in the same
  // session — replaces rather than merges.
  const isDifferentBooking =
    (Boolean(previous.uid) && Boolean(next.uid) && previous.uid !== next.uid) ||
    (previous.start !== null &&
      next.start !== null &&
      previous.start.getTime() !== next.start.getTime());
  if (isDifferentBooking) return next;

  return {
    title: next.title,
    start: next.start ?? previous.start,
    duration: next.duration ?? previous.duration,
    location: next.location ?? previous.location,
    uid: next.uid ?? previous.uid,
    participants:
      next.participants.length > previous.participants.length
        ? next.participants
        : previous.participants,
    // A reschedule stays a reschedule even if a plain success event follows.
    rescheduled: next.rescheduled || previous.rescheduled,
  };
};

// "in 3 days", "tomorrow", "in 2 hours" — the one detail people actually want
// after booking that Cal's own screen never shows.
export const relativeToNow = (target: Date, now: Date) => {
  const diffMs = target.getTime() - now.getTime();
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const mins = Math.round(diffMs / 60_000);

  if (Math.abs(mins) < 60) return rtf.format(mins, "minute");
  const hours = Math.round(mins / 60);
  if (Math.abs(hours) < 24) return rtf.format(hours, "hour");
  return rtf.format(Math.round(hours / 24), "day");
};

// "AB" from "Ada Byron", "A" from a single word. Drives the avatar chips.
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("");

// Compact UTC stamp Google Calendar expects: 20260722T100000Z
const gcalStamp = (d: Date) => d.toISOString().replace(/[-:]|\.\d{3}/g, "");

// "Add to Google Calendar" link for a confirmed booking. Cal already emails an
// invite; this is the one-click path for anyone who ignores email.
export const googleCalendarUrl = ({
  title,
  start,
  duration,
  details,
}: {
  title: string;
  start: Date;
  duration: number;
  details?: string;
}) => {
  const end = new Date(start.getTime() + duration * 60_000);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates: `${gcalStamp(start)}/${gcalStamp(end)}`,
  });
  if (details) params.set("details", details);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};
